//TODO: split this into server and client message queue

import { System } from './base/System.js';
import { SystemMessage, ReceivedMessage, LocalSystemMessage, ReceivedLocalSystemMessage } from './types.js';
import { clearObj } from './utils.js';


type QueuedMessage = SystemMessage | ReceivedMessage | LocalSystemMessage
type SystemMessageLookup = { [systemName: string]: Array<SystemMessage | ReceivedMessage> }
type SystemLookup = { [systemName: string]: System }

/**
 * Queues and dispatches local system messages.
 */
export class MessageQueue {
    readonly systemNames: Array<string | number> = []
    readonly _systems: SystemLookup = {}
    readonly _messages: SystemMessageLookup = {}

    constructor() {
        this._systems = {} as SystemLookup;
        this.systemNames = [];
        this._messages = {} as SystemMessageLookup;
    }

    get systems() {
        return this._systems;
    }

    get messages() {
        return this._messages;
    }

    public clear() {
        clearObj(this._messages);
        clearObj(this._systems);
        this.systemNames.length = 0;
    }

    public removeSystem(systemName) {
        if(systemName in this._messages) {
            this._messages[systemName].length = 0;
        }
        delete this._messages[systemName];
        delete this._systems[systemName];

        const index = this.systemNames.indexOf(systemName);
        if(index >= 0) {
            this.systemNames.splice(index, 1);
        }
    }

    public removeAllSystemsAndMessages() {
        for(let systemName in this._systems) {
            delete this._systems[systemName];
            this._messages[systemName].length = 0;
            delete this._messages[systemName];
            this.systemNames.length = 0;
        }
    }

    public removeAllMessages() {
        for(let systemName in this._systems) {
            this._messages[systemName].length = 0;
        }
    }

    public addSystem(system: System) {
        if(system.name as unknown as string in this._systems) {
            throw new Error(`Already added system ${system.name} to message queue.`);
        }
    
        if(system.onLocalSystemMessage) {
            this._systems[system.name as unknown as string] = system;
            this._messages[system.name as unknown as string] = [];
            this.systemNames.push(system.name as unknown as string);
        }
    }

    public add(message: LocalSystemMessage) {
        if(Array.isArray(message.to)) {
            for(let i = 0; i < message.to.length; i++) {
                if(!(message.to[i] as unknown as string in this._systems)) {
                    console.warn('trying to dispatch client message:', message.type, 'to a nonexistent system name', message.to[i]);
                    continue;
                }
                this._messages[message.to[i] as unknown as string].push(message);
            }
            return;
        }
        if(!(message.to as unknown as string in this._systems)) {
            console.warn('trying to dispatch client message:', message.type, 'to a nonexistent system name', message.to);
        } else {
            this._messages[message.to as unknown as string].push(message);
        }
    }

    public addAll(message: ReceivedMessage) {
        const systemsLength = this.systemNames.length;
        for(let i = 0; i < systemsLength; i++) {
            this._messages[this.systemNames[i]].push(message);
        }
    };

    public instantDispatch(message: LocalSystemMessage) {
        if(Array.isArray(message.to)) {
            for(let i = 0; i < message.to.length; i++) {
                this._systems[message.to[i]]?.onLocalSystemMessage(message);                
            }
        } else {
            this._systems[message.to]?.onLocalSystemMessage(message);                
        }
    }

    public instantDispatchAll(message: ReceivedLocalSystemMessage) {
        for(let i = 0; i < this.systemNames.length; i++) {
            this._systems[this.systemNames[i]].onLocalSystemMessage(message);
        }
    }

    public dispatch(systemName) {
        const messages = this._messages[systemName];
        if (messages) {
            for(let i = 0; i < messages.length; i++) {
                this._systems[systemName]?.onLocalSystemMessage(messages[i] as LocalSystemMessage);
            }
            messages.length = 0;
        }
    }
}